import React, { useState, useEffect } from 'react';
import { Home, Plus, Trash2, Edit2, X, Save, Image as ImageIcon, Users, DollarSign, AlertCircle, Clock, Upload, Loader2, ChevronDown, ChevronUp, Bell, Ban, Calendar, Repeat } from 'lucide-react';
import { useI18n } from '@/shared/i18n';
import { useServices, useDeleteConfirmation, useSection } from '@/app/providers';
import { Amenity, ChargeTiming, WeeklyScheduleDay, BlackoutDate, CancellationPolicyTier, ReservationLimitPeriod } from '@/shared/types';
import { AmenityPricingMode } from '@yawi/types';
import type { AmenitySectionPrice } from '@yawi/types';
import { useAbility } from '@/modules/identity/application/ability';
import { SectionMultiSelect } from '@/modules/sections/ui/components/SectionMultiSelect';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import { useCrudNotifications, useFileUpload } from '@/shared/hooks';

const DAY_NAMES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

const DEFAULT_WEEKLY_SCHEDULE: WeeklyScheduleDay[] = [
  { isClosed: false, openTime: '08:00', closeTime: '22:00' },
  { isClosed: false, openTime: '08:00', closeTime: '22:00' },
  { isClosed: false, openTime: '08:00', closeTime: '22:00' },
  { isClosed: false, openTime: '08:00', closeTime: '22:00' },
  { isClosed: false, openTime: '08:00', closeTime: '22:00' },
  { isClosed: false, openTime: '09:00', closeTime: '18:00' },
  { isClosed: true,  openTime: '00:00', closeTime: '00:00' },
];

const DEFAULT_CREATE: Partial<Amenity> = {
  requiresDeposit: false,
  depositAmount: 0,
  costPerHour: 0,
  minHours: 1,
  maxHours: 4,
  capacity: 10,
  rules: '',
  termsAndConditions: '',
  chargeTiming: ChargeTiming.ON_CONFIRM,
  isActive: true,
  requiresApproval: false,
  autoConfirmIfPaid: false,
  accessControlEnabled: false,
  timezone: 'America/Mexico_City',
  weeklySchedule: DEFAULT_WEEKLY_SCHEDULE.map(d => ({ ...d })),
  blackoutDates: [],
  cancellationPolicy: {
    tiers: [
      { minHoursBefore: 72, rentalRefundPercent: 100, depositRefundPercent: 100 },
      { minHoursBefore: 24, rentalRefundPercent: 50,  depositRefundPercent: 100 },
      { minHoursBefore: 0,  rentalRefundPercent: 0,   depositRefundPercent: 0   },
    ],
  },
  reminderConfig: { send24h: true, send2h: true },
  reservationFrequencyLimit: null,
  sectionIds: [],
  pricingMode: AmenityPricingMode.PER_HOUR,
  sectionPrices: [],
};

const toNonNegativeNumber = (value: unknown): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

const normalizeAmenityPayload = (amenity: Partial<Amenity>): Partial<Amenity> => ({
  ...amenity,
  capacity: toNonNegativeNumber(amenity.capacity),
  minHours: toNonNegativeNumber(amenity.minHours),
  maxHours: toNonNegativeNumber(amenity.maxHours),
  costPerHour: toNonNegativeNumber(amenity.costPerHour),
  depositAmount: amenity.requiresDeposit ? toNonNegativeNumber(amenity.depositAmount) : 0,
});

const TIMEZONES = [
  'America/Mexico_City',
  'America/Tijuana',
  'America/Chihuahua',
  'America/Hermosillo',
  'America/Cancun',
  'America/Monterrey',
];

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const Section = ({ title, icon, children, defaultOpen = false }: SectionProps) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          {icon}
          {title}
        </div>
        {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>
      {open && <div className="p-4 space-y-4">{children}</div>}
    </div>
  );
};

export const AdminAmenityList = () => {
  const { t } = useI18n();
  const { confirmDelete } = useDeleteConfirmation();
  const { amenityRepo } = useServices();
  const { hasSections, sections, isSectionAdmin } = useSection();
  const ability = useAbility();
  const canUseAmenityAction = (action: 'create' | 'update' | 'delete') =>
    ability.can('manage', 'all') || ability.can(action, 'Amenity');
  const canCreateAmenity = canUseAmenityAction('create');
  const canUpdateAmenity = canUseAmenityAction('update');
  const canDeleteAmenity = canUseAmenityAction('delete');
  const { withCreateNotification, withUpdateNotification, withDeleteNotification } = useCrudNotifications({
    entityName: 'Amenidad',
  });

  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState<Partial<Amenity>>({});
  const [isEditMode, setIsEditMode] = useState(false);

  const { upload, isUploading } = useFileUpload('amenities', { allowedTypes: ['image/*'] });

  useEffect(() => { fetchAmenities(); }, []);

  const fetchAmenities = async () => {
    setLoading(true);
    try {
      const { data } = await amenityRepo.getAmenities({ limit: 100 });
      setAmenities(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    if (!canCreateAmenity) return;
    setEditingAmenity(JSON.parse(JSON.stringify(DEFAULT_CREATE)));
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (amenity: Amenity) => {
    if (!canUpdateAmenity) return;
    const copy = JSON.parse(JSON.stringify(amenity)) as Partial<Amenity>;
    if (!copy.weeklySchedule) copy.weeklySchedule = DEFAULT_WEEKLY_SCHEDULE.map(d => ({ ...d }));
    if (!copy.blackoutDates) copy.blackoutDates = [];
    if (!copy.cancellationPolicy) copy.cancellationPolicy = { tiers: [] };
    if (!copy.reminderConfig) copy.reminderConfig = { send24h: false, send2h: false };
    if (!copy.sectionIds) copy.sectionIds = [];
    if (!copy.sectionPrices) copy.sectionPrices = [];
    setEditingAmenity(copy);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!canDeleteAmenity) return;
    await confirmDelete({
      itemName: name,
      itemType: 'Amenidad',
      onConfirm: async () => {
        await withDeleteNotification(() => amenityRepo.deleteAmenity(id));
        fetchAmenities();
      }
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditMode ? !canUpdateAmenity : !canCreateAmenity) return;
    try {
      const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, ...payload } = editingAmenity as Amenity & { deletedAt?: string };
      delete (payload as Record<string, unknown>).deletedAt;
      const normalizedPayload = normalizeAmenityPayload(payload);
      if (isSectionAdmin) {
        delete normalizedPayload.sectionPrices; // sin el campo, el backend conserva los precios
      } else {
        // Si se desmarcó una sección, su precio ya no aplica (el backend lo rechazaría).
        const allowed = new Set(priceableSections.map(s => s.id));
        normalizedPayload.sectionPrices = (normalizedPayload.sectionPrices ?? []).filter(p => allowed.has(p.sectionId));
      }
      if (isEditMode && editingAmenity.id) {
        await withUpdateNotification(() => amenityRepo.updateAmenity(editingAmenity.id!, normalizedPayload));
      } else {
        await withCreateNotification(() => amenityRepo.createAmenity(normalizedPayload as Omit<Amenity, 'id' | 'createdAt' | 'updatedAt'>));
      }
      setIsModalOpen(false);
      fetchAmenities();
    } catch {
      // Error ya mostrado por el hook
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const result = await upload(file);
      if (result) {
        setEditingAmenity(prev => ({ ...prev, imageUrl: result.url, imageFileId: result.id }));
      }
    }
  };

  // Helpers for nested state
  const setWeekDay = (idx: number, patch: Partial<WeeklyScheduleDay>) => {
    setEditingAmenity(prev => {
      const schedule = [...(prev.weeklySchedule ?? DEFAULT_WEEKLY_SCHEDULE.map(d => ({ ...d })))];
      schedule[idx] = { ...schedule[idx], ...patch };
      return { ...prev, weeklySchedule: schedule };
    });
  };

  const addBlackout = () => {
    setEditingAmenity(prev => ({
      ...prev,
      blackoutDates: [...(prev.blackoutDates ?? []), { date: '', reason: '' }],
    }));
  };

  const setBlackout = (idx: number, patch: Partial<BlackoutDate>) => {
    setEditingAmenity(prev => {
      const dates = [...(prev.blackoutDates ?? [])];
      dates[idx] = { ...dates[idx], ...patch };
      return { ...prev, blackoutDates: dates };
    });
  };

  const removeBlackout = (idx: number) => {
    setEditingAmenity(prev => ({
      ...prev,
      blackoutDates: (prev.blackoutDates ?? []).filter((_, i) => i !== idx),
    }));
  };

  const addTier = () => {
    setEditingAmenity(prev => ({
      ...prev,
      cancellationPolicy: {
        tiers: [...(prev.cancellationPolicy?.tiers ?? []), { minHoursBefore: 0, rentalRefundPercent: 0, depositRefundPercent: 0 }],
      },
    }));
  };

  const setTier = (idx: number, patch: Partial<CancellationPolicyTier>) => {
    setEditingAmenity(prev => {
      const tiers = [...(prev.cancellationPolicy?.tiers ?? [])];
      tiers[idx] = { ...tiers[idx], ...patch };
      return { ...prev, cancellationPolicy: { tiers } };
    });
  };

  // Una amenidad restringida solo cobra precio propio en sus secciones.
  const priceableSections = editingAmenity.sectionIds?.length
    ? sections.filter(s => editingAmenity.sectionIds!.includes(s.id))
    : sections;

  const addSectionPrice = () => {
    setEditingAmenity(prev => ({
      ...prev,
      sectionPrices: [...(prev.sectionPrices ?? []), { sectionId: '', pricingMode: AmenityPricingMode.PER_HOUR, price: 0, depositAmount: null }],
    }));
  };

  const setSectionPrice = (idx: number, patch: Partial<AmenitySectionPrice>) => {
    setEditingAmenity(prev => {
      const prices = [...(prev.sectionPrices ?? [])];
      prices[idx] = { ...prices[idx], ...patch };
      return { ...prev, sectionPrices: prices };
    });
  };

  const removeSectionPrice = (idx: number) => {
    setEditingAmenity(prev => ({
      ...prev,
      sectionPrices: (prev.sectionPrices ?? []).filter((_, i) => i !== idx),
    }));
  };

  const removeTier = (idx: number) => {
    setEditingAmenity(prev => ({
      ...prev,
      cancellationPolicy: { tiers: (prev.cancellationPolicy?.tiers ?? []).filter((_, i) => i !== idx) },
    }));
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden animate-in fade-in duration-300">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
          <h3 className="font-bold text-slate-800 text-lg">{t('admin.amenities.title')}</h3>
          {canCreateAmenity && (
            <Button onClick={handleOpenCreate} icon={<Plus size={16}/>}>
              {t('admin.amenities.create')}
            </Button>
          )}
        </div>

        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="animate-spin text-slate-400" size={32} /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
            {amenities.map((amenity) => (
              <div key={amenity.id} className="border border-slate-200 rounded-xl overflow-hidden group hover:shadow-md transition-shadow">
                <div className="h-40 bg-slate-100 relative overflow-hidden">
                  {amenity.imageUrl ? (
                    <img src={amenity.imageUrl} alt={amenity.name} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300">
                      <ImageIcon size={48} />
                    </div>
                  )}
                  {(canUpdateAmenity || canDeleteAmenity) && (
                    <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 p-1 rounded-lg backdrop-blur-sm">
                      {canUpdateAmenity && (
                        <button onClick={() => handleOpenEdit(amenity)} className="p-1.5 text-slate-600 hover:text-primary-600">
                          <Edit2 size={16} />
                        </button>
                      )}
                      {canDeleteAmenity && (
                        <button onClick={() => handleDelete(amenity.id, amenity.name)} className="p-1.5 text-slate-600 hover:text-red-600">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <h4 className="font-bold text-slate-800">{amenity.name}</h4>
                      {amenity.code && <span className="text-[10px] text-slate-400 font-mono">{amenity.code}</span>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {amenity.requiresApproval && (
                        <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full border border-amber-200">
                          Aprobación
                        </span>
                      )}
                      {!amenity.isActive && (
                        <span className="text-[9px] font-bold bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full border border-slate-200">
                          Inactiva
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center text-xs text-slate-600 gap-2">
                      <Users size={14} className="text-slate-400" />
                      <span>Cap: {amenity.capacity} pers.</span>
                      <span className="text-slate-300">|</span>
                      <Clock size={14} className="text-slate-400" />
                      <span>Max: {amenity.maxHours}h</span>
                    </div>
                    <div className="flex items-center text-xs text-slate-600 gap-2">
                      <DollarSign size={14} className="text-slate-400" />
                      <span>
                        Costo: ${amenity.costPerHour}{amenity.pricingMode === AmenityPricingMode.FLAT ? ' por reserva' : '/hr'}
                        {!!amenity.sectionPrices?.length && ` · ${amenity.sectionPrices.length} por sección`}
                      </span>
                    </div>
                    {amenity.requiresDeposit && (
                      <div className="flex items-center text-xs text-amber-600 gap-2 font-medium">
                        <AlertCircle size={14} />
                        <span>Depósito: ${amenity.depositAmount}</span>
                      </div>
                    )}
                    {amenity.weeklySchedule && (
                      <div className="flex items-center text-xs text-slate-500 gap-1">
                        <Clock size={12} className="text-slate-400" />
                        <span>{amenity.weeklySchedule.filter(d => !d.isClosed).length} días activos</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-5 border-b border-slate-100 shrink-0">
              <h3 className="font-bold text-slate-800 text-lg">{isEditMode ? t('common.edit') : t('admin.amenities.create')}</h3>
              <button type="button" onClick={() => setIsModalOpen(false)}><X size={20} className="text-slate-400"/></button>
            </div>

            <form onSubmit={handleSave} className="p-6 overflow-y-auto">
              <div className="space-y-4">

                {/* ─── Básicos ─── */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="col-span-3">
                    <Input
                      label={t('admin.amenities.name')}
                      value={editingAmenity.name || ''}
                      onChange={e => setEditingAmenity({ ...editingAmenity, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Código (3)</label>
                    <input
                      type="text"
                      maxLength={3}
                      placeholder="SAL"
                      value={editingAmenity.code || ''}
                      onChange={e => setEditingAmenity({ ...editingAmenity, code: e.target.value.toUpperCase() })}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-100 uppercase"
                    />
                  </div>
                </div>

                {/* ─── Imagen ─── */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('admin.amenities.image')}</label>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                      {editingAmenity.imageUrl ? (
                        <img src={editingAmenity.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <ImageIcon className="text-slate-300" />
                      )}
                    </div>
                    <label className={`cursor-pointer inline-flex items-center justify-center px-4 py-2 border border-slate-300 shadow-sm text-sm font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 transition-colors flex-1 ${isUploading ? 'opacity-50 cursor-wait' : ''}`}>
                      {isUploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
                      {isUploading ? 'Subiendo...' : t('admin.amenities.upload')}
                      <input type="file" className="hidden" accept="image/*" disabled={isUploading} onChange={handleImageUpload} />
                    </label>
                  </div>
                </div>

                {/* ─── Capacidad y horas ─── */}
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    label={t('admin.amenities.capacity')}
                    type="number"
                    value={editingAmenity.capacity ?? ''}
                    onChange={e => setEditingAmenity({ ...editingAmenity, capacity: parseInt(e.target.value) || 0 })}
                    required
                  />
                  <Input
                    label={t('admin.amenities.min_hours')}
                    type="number"
                    value={editingAmenity.minHours ?? ''}
                    onChange={e => setEditingAmenity({ ...editingAmenity, minHours: parseInt(e.target.value) || 0 })}
                    required
                  />
                  <Input
                    label={t('admin.amenities.max_hours')}
                    type="number"
                    value={editingAmenity.maxHours ?? ''}
                    onChange={e => setEditingAmenity({ ...editingAmenity, maxHours: parseInt(e.target.value) || 0 })}
                    required
                  />
                </div>

                {/* ─── Cobros ─── */}
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Tipo de cobro</label>
                    <select
                      value={editingAmenity.pricingMode ?? AmenityPricingMode.PER_HOUR}
                      onChange={e => setEditingAmenity({ ...editingAmenity, pricingMode: e.target.value as AmenityPricingMode })}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
                    >
                      <option value={AmenityPricingMode.PER_HOUR}>Por hora</option>
                      <option value={AmenityPricingMode.FLAT}>Total por reserva (sin importar las horas)</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label={editingAmenity.pricingMode === AmenityPricingMode.FLAT ? 'Costo por reserva' : t('admin.amenities.cost')}
                      type="number"
                      value={editingAmenity.costPerHour ?? ''}
                      onChange={e => setEditingAmenity({ ...editingAmenity, costPerHour: parseInt(e.target.value) || 0 })}
                      icon={<DollarSign size={14}/>}
                    />
                    <div className="flex flex-col justify-end pb-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="deposit"
                          checked={editingAmenity.requiresDeposit || false}
                          onChange={e => setEditingAmenity({
                            ...editingAmenity,
                            requiresDeposit: e.target.checked,
                            depositAmount: e.target.checked ? toNonNegativeNumber(editingAmenity.depositAmount) : 0,
                          })}
                          className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                        />
                        <label htmlFor="deposit" className="text-sm text-slate-700 font-medium select-none cursor-pointer">
                          {t('admin.amenities.deposit')}
                        </label>
                      </div>
                    </div>
                  </div>

                  {editingAmenity.requiresDeposit && (
                    <Input
                      label={t('admin.amenities.deposit_amt')}
                      type="number"
                      min={0}
                      value={editingAmenity.depositAmount ?? 0}
                      onChange={e => setEditingAmenity({ ...editingAmenity, depositAmount: toNonNegativeNumber(e.target.value) })}
                      icon={<AlertCircle size={14}/>}
                      className="bg-white"
                    />
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Momento de Cobro</label>
                    <select
                      value={editingAmenity.chargeTiming || ChargeTiming.ON_CONFIRM}
                      onChange={e => setEditingAmenity({ ...editingAmenity, chargeTiming: e.target.value as ChargeTiming })}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
                    >
                      <option value={ChargeTiming.ON_CONFIRM}>Al confirmar reservación</option>
                      <option value={ChargeTiming.ON_REQUEST}>Al solicitar reservación</option>
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      {editingAmenity.chargeTiming === ChargeTiming.ON_REQUEST
                        ? 'El cargo se genera al crear la reservación'
                        : 'El cargo se genera cuando el admin confirme'}
                    </p>
                  </div>
                </div>

                {/* ─── Flags ─── */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: 'requiresApproval', label: 'Requiere aprobación', desc: 'Las reservaciones quedan en Pendiente', field: 'requiresApproval' as const },
                    { id: 'isActive', label: 'Activa', desc: 'Visible para los residentes', field: 'isActive' as const },
                    { id: 'autoConfirmIfPaid', label: 'Auto-confirmar si pagó', desc: 'Solo si requiere aprobación', field: 'autoConfirmIfPaid' as const },
                    { id: 'accessControlEnabled', label: 'Control de acceso', desc: 'Integra puertas inteligentes', field: 'accessControlEnabled' as const },
                  ].map(({ id, label, desc, field }) => (
                    <div key={id} className="flex items-center gap-2 p-3 border border-slate-200 rounded-lg">
                      <input
                        type="checkbox"
                        id={id}
                        checked={(editingAmenity[field] as boolean) ?? false}
                        onChange={e => setEditingAmenity({ ...editingAmenity, [field]: e.target.checked })}
                        className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 shrink-0"
                      />
                      <div>
                        <label htmlFor={id} className="text-sm font-medium text-slate-700 cursor-pointer select-none block">{label}</label>
                        <p className="text-[11px] text-slate-400">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* ─── Secciones ─── */}
                {hasSections && (
                  <Section title="Secciones" icon={<Users size={15} />}>
                    <SectionMultiSelect
                      value={editingAmenity.sectionIds ?? []}
                      onChange={(sectionIds) => setEditingAmenity(prev => ({ ...prev, sectionIds }))}
                      resourceLabel="amenidad"
                      idPrefix="amenity"
                    />
                  </Section>
                )}

                {/* ─── Precios por sección ─── */}
                {/* ponytail: solo el admin global; un admin de sección reenviaría
                    precios de secciones ajenas y el backend lo rechaza. */}
                {hasSections && !isSectionAdmin && (
                  <Section title="Precios por sección" icon={<DollarSign size={15} />}>
                    <p className="text-xs text-slate-500">
                      Las secciones sin precio propio usan el cobro base. Depósito vacío: el de la amenidad.
                    </p>
                    {(editingAmenity.sectionPrices ?? []).map((row, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr_1fr_6rem_6rem_auto] gap-2 items-end">
                        <select
                          aria-label="Sección"
                          value={row.sectionId}
                          onChange={e => setSectionPrice(idx, { sectionId: e.target.value })}
                          className="w-full rounded-lg border border-slate-300 bg-white px-2 py-2.5 text-sm"
                          required
                        >
                          <option value="">Sección</option>
                          {priceableSections
                            .filter(s => s.id === row.sectionId || !(editingAmenity.sectionPrices ?? []).some(p => p.sectionId === s.id))
                            .map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <select
                          aria-label="Tipo de cobro"
                          value={row.pricingMode}
                          onChange={e => setSectionPrice(idx, { pricingMode: e.target.value as AmenityPricingMode })}
                          className="w-full rounded-lg border border-slate-300 bg-white px-2 py-2.5 text-sm"
                        >
                          <option value={AmenityPricingMode.PER_HOUR}>Por hora</option>
                          <option value={AmenityPricingMode.FLAT}>Total por reserva</option>
                        </select>
                        <Input
                          label="Monto"
                          type="number"
                          min={0}
                          value={row.price}
                          onChange={e => setSectionPrice(idx, { price: toNonNegativeNumber(e.target.value) })}
                        />
                        <Input
                          label="Depósito"
                          type="number"
                          min={0}
                          value={row.depositAmount ?? ''}
                          onChange={e => setSectionPrice(idx, { depositAmount: e.target.value === '' ? null : toNonNegativeNumber(e.target.value) })}
                        />
                        <button type="button" onClick={() => removeSectionPrice(idx)} className="p-2.5 text-slate-400 hover:text-red-600" aria-label="Quitar precio">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                    {(editingAmenity.sectionPrices ?? []).length < priceableSections.length && (
                      <Button type="button" variant="secondary" icon={<Plus size={14} />} onClick={addSectionPrice}>
                        Agregar precio por sección
                      </Button>
                    )}
                  </Section>
                )}

                {/* ─── Horario semanal ─── */}
                <Section title="Horario semanal" icon={<Clock size={15} />}>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Zona horaria</label>
                    <select
                      value={editingAmenity.timezone || 'America/Mexico_City'}
                      onChange={e => setEditingAmenity({ ...editingAmenity, timezone: e.target.value })}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
                    >
                      {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    {(editingAmenity.weeklySchedule ?? DEFAULT_WEEKLY_SCHEDULE).map((day, idx) => (
                      <div key={idx} className="grid grid-cols-[80px_1fr] items-center gap-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`day-open-${idx}`}
                            checked={!day.isClosed}
                            onChange={e => setWeekDay(idx, { isClosed: !e.target.checked })}
                            className="w-3.5 h-3.5 text-primary-600 rounded"
                          />
                          <label htmlFor={`day-open-${idx}`} className="text-xs font-medium text-slate-600 cursor-pointer select-none">
                            {DAY_NAMES[idx]}
                          </label>
                        </div>
                        {day.isClosed ? (
                          <span className="text-xs text-slate-400 italic">Cerrado</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <input
                              type="time"
                              value={day.openTime}
                              onChange={e => setWeekDay(idx, { openTime: e.target.value })}
                              className="rounded border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary-300"
                            />
                            <span className="text-slate-400 text-xs">a</span>
                            <input
                              type="time"
                              value={day.closeTime}
                              onChange={e => setWeekDay(idx, { closeTime: e.target.value })}
                              className="rounded border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary-300"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Section>

                {/* ─── Fechas bloqueadas ─── */}
                <Section title="Fechas bloqueadas" icon={<Ban size={15} />}>
                  <div className="space-y-2">
                    {(editingAmenity.blackoutDates ?? []).map((bd, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          type="date"
                          value={bd.date}
                          onChange={e => setBlackout(idx, { date: e.target.value })}
                          className="rounded border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-300"
                        />
                        <input
                          type="text"
                          placeholder="Motivo (opcional)"
                          value={bd.reason || ''}
                          onChange={e => setBlackout(idx, { reason: e.target.value })}
                          className="flex-1 rounded border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-300"
                        />
                        <button type="button" onClick={() => removeBlackout(idx)} className="text-red-400 hover:text-red-600 p-1">
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <Button type="button" variant="secondary" icon={<Plus size={14}/>} onClick={addBlackout}>
                    Agregar fecha
                  </Button>
                </Section>

                {/* ─── Límite de reservaciones ─── */}
                <Section title="Límite de reservaciones" icon={<Repeat size={15} />}>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="reservationLimit"
                      checked={!!editingAmenity.reservationFrequencyLimit}
                      onChange={e => setEditingAmenity(prev => ({
                        ...prev,
                        reservationFrequencyLimit: e.target.checked
                          ? { maxReservations: 1, period: ReservationLimitPeriod.WEEK }
                          : null,
                      }))}
                      className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                    />
                    <label htmlFor="reservationLimit" className="text-sm text-slate-700 font-medium select-none cursor-pointer">
                      Limitar reservas por unidad
                    </label>
                  </div>
                  {editingAmenity.reservationFrequencyLimit && (
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        label="Máximo de reservas"
                        type="number"
                        min={1}
                        value={editingAmenity.reservationFrequencyLimit.maxReservations}
                        onChange={e => setEditingAmenity(prev => ({
                          ...prev,
                          reservationFrequencyLimit: {
                            ...(prev.reservationFrequencyLimit ?? { maxReservations: 1, period: ReservationLimitPeriod.WEEK }),
                            maxReservations: parseInt(e.target.value) || 1,
                          },
                        }))}
                      />
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Periodo</label>
                        <select
                          value={editingAmenity.reservationFrequencyLimit.period}
                          onChange={e => setEditingAmenity(prev => ({
                            ...prev,
                            reservationFrequencyLimit: {
                              ...(prev.reservationFrequencyLimit ?? { maxReservations: 1, period: ReservationLimitPeriod.WEEK }),
                              period: e.target.value as ReservationLimitPeriod,
                            },
                          }))}
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
                        >
                          <option value={ReservationLimitPeriod.WEEK}>Semana</option>
                          <option value={ReservationLimitPeriod.MONTH}>Mes</option>
                        </select>
                      </div>
                    </div>
                  )}
                </Section>

                {/* ─── Política de cancelación ─── */}
                <Section title="Política de cancelación" icon={<Calendar size={15} />}>
                  <p className="text-xs text-slate-500">Define los tramos de reembolso según horas de anticipación.</p>
                  <div className="space-y-2">
                    {(editingAmenity.cancellationPolicy?.tiers ?? []).map((tier, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                        <div>
                          <label className="text-[10px] text-slate-500 block mb-0.5">Horas antes</label>
                          <input
                            type="number"
                            min={0}
                            value={tier.minHoursBefore}
                            onChange={e => setTier(idx, { minHoursBefore: parseInt(e.target.value) || 0 })}
                            className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-300"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-slate-500 block mb-0.5">% Renta</label>
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={tier.rentalRefundPercent}
                            onChange={e => setTier(idx, { rentalRefundPercent: parseInt(e.target.value) || 0 })}
                            className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-300"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-slate-500 block mb-0.5">% Depósito</label>
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={tier.depositRefundPercent}
                            onChange={e => setTier(idx, { depositRefundPercent: parseInt(e.target.value) || 0 })}
                            className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-300"
                          />
                        </div>
                        <button type="button" onClick={() => removeTier(idx)} className="text-red-400 hover:text-red-600 p-1 mt-4">
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <Button type="button" variant="secondary" icon={<Plus size={14}/>} onClick={addTier}>
                    Agregar tramo
                  </Button>
                </Section>

                {/* ─── Recordatorios ─── */}
                <Section title="Recordatorios" icon={<Bell size={15} />}>
                  <div className="space-y-2">
                    {[
                      { id: 'send24h', label: 'Enviar recordatorio 24h antes' },
                      { id: 'send2h',  label: 'Enviar recordatorio 2h antes' },
                    ].map(({ id, label }) => (
                      <div key={id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={id}
                          checked={(editingAmenity.reminderConfig as Record<string, boolean> | undefined)?.[id] ?? false}
                          onChange={e => setEditingAmenity(prev => ({
                            ...prev,
                            reminderConfig: { ...prev.reminderConfig, [id]: e.target.checked },
                          }))}
                          className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                        />
                        <label htmlFor={id} className="text-sm text-slate-700 select-none cursor-pointer">{label}</label>
                      </div>
                    ))}
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Horas personalizadas (separadas por coma, ej: 48, 12)</label>
                      <input
                        type="text"
                        placeholder="48, 12"
                        value={(editingAmenity.reminderConfig?.sendCustomHours ?? []).join(', ')}
                        onChange={e => {
                          const hours = e.target.value
                            .split(',')
                            .map(s => parseInt(s.trim()))
                            .filter(n => !isNaN(n) && n > 0);
                          setEditingAmenity(prev => ({
                            ...prev,
                            reminderConfig: { ...prev.reminderConfig, sendCustomHours: hours },
                          }));
                        }}
                        className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-300"
                      />
                    </div>
                  </div>
                </Section>

                {/* ─── Reglas y T&C ─── */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('admin.amenities.rules')}</label>
                    <textarea
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
                      rows={3}
                      value={editingAmenity.rules || ''}
                      onChange={e => setEditingAmenity({ ...editingAmenity, rules: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('admin.amenities.terms')}</label>
                    <textarea
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
                      rows={3}
                      value={editingAmenity.termsAndConditions || ''}
                      onChange={e => setEditingAmenity({ ...editingAmenity, termsAndConditions: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-6">
                <Button type="button" variant="secondary" className="flex-1" onClick={() => setIsModalOpen(false)}>
                  {t('common.close')}
                </Button>
                <Button type="submit" variant="primary" className="flex-1" icon={<Save size={16}/>}>
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
